import { useMemo, useState } from 'react'
import { useHostUsageMinutes } from '@/api/queries'
import type { HostUsagePoint } from '@/api/types'
import { TimeSeries } from '@/components/charts/TimeSeries'
import { DataTable, type Column } from '@/components/DataTable/DataTable'
import { EmptyState, ErrorState } from '@/components/ui/EmptyState'
import { Modal } from '@/components/ui/Modal'
import { LoadingBlock } from '@/components/ui/Spinner'
import { colors } from '@/lib/colors'
import { dateTime, isoDate, toDate } from '@/lib/format'

const SERIES = [
  { key: 'requests', name: 'Requests', color: colors.info },
  { key: 'errors', name: 'Errors', color: colors.danger },
  { key: 'ips', name: 'IPs', color: colors.teal },
]

/** Per-minute traffic of one host; the brush on the chart narrows the table below it. */
export function HostDetailsDialog({ host, onClose }: { host: string; onClose: () => void }) {
  const [date, setDate] = useState(() => isoDate(new Date()))
  const { data, isLoading, error } = useHostUsageMinutes(host, date)
  const [range, setRange] = useState<[number, number] | null>(null)

  // Several products/providers may report the same minute: the chart sums them, the table keeps the detail.
  const points = useMemo(() => {
    const byMinute = new Map<number, { time: number; requests: number; errors: number; ips: number }>()
    for (const p of data ?? []) {
      const time = toDate(p.date).getTime()
      const bucket = byMinute.get(time) ?? { time, requests: 0, errors: 0, ips: 0 }
      bucket.requests += p.numberOfRequests
      bucket.errors += p.numberOfErrors
      bucket.ips += p.numberOfIps
      byMinute.set(time, bucket)
    }
    return [...byMinute.values()].sort((a, b) => a.time - b.time)
  }, [data])

  const visible = useMemo(() => {
    if (!data || !range) return data
    return data.filter((p) => {
      const t = toDate(p.date).getTime()
      return t >= range[0] && t <= range[1]
    })
  }, [data, range])

  const columns: Column<HostUsagePoint>[] = [
    { key: 'date', header: 'Time', render: (p) => dateTime(p.date, 'HH:mm') },
    { key: 'product', header: 'Product', render: (p) => p.product ?? '–' },
    { key: 'provider', header: 'Provider', render: (p) => p.provider ?? '–' },
    { key: 'numberOfRequests', header: 'Requests', align: 'right' },
    { key: 'numberOfErrors', header: 'Errors', align: 'right' },
    { key: 'numberOfIps', header: 'IPs', align: 'right' },
    { key: 'averageTimePerRequestMs', header: 'Avg ms', align: 'right', render: (p) => Math.round(p.averageTimePerRequestMs) },
    { key: 'sizeInMb', header: 'MB', align: 'right', render: (p) => p.sizeInMb.toFixed(1) },
  ]

  const picker = (
    <input
      type="date"
      className="htl-text-input"
      value={date}
      max={isoDate(new Date())}
      aria-label="Day"
      onChange={(e) => {
        setRange(null)
        setDate(e.target.value)
      }}
    />
  )

  return (
    <Modal title={`${host} · requests per minute`} onClose={onClose} size="xl" headerExtra={picker}>
      {error ? (
        <ErrorState error={error} />
      ) : isLoading ? (
        <LoadingBlock />
      ) : points.length === 0 ? (
        <EmptyState>No traffic recorded on {date}</EmptyState>
      ) : (
        <TimeSeries data={points} series={SERIES} height={260} onRangeChange={(from, to) => setRange([from, to])} />
      )}
      <DataTable columns={columns} rows={visible} rowKey={(p) => `${p.date}:${p.product}:${p.provider}`} loading={isLoading} error={error} searchable searchPlaceholder="Search a product or provider" dense maxHeight="320px" initialSort={{ key: 'date', direction: 'desc' }} />
    </Modal>
  )
}
